/**
 * Browser API Client
 *
 * Browser-specific implementation of ApiClient interface.
 * Features:
 * - Reads token from cookie using js-cookie
 * - Reads sub-account UUID from sessionStorage
 * - Can show toast notifications for errors via onError callback
 */

import Cookies from 'js-cookie';

import { BaseApiClient, type BaseApiClientConfig } from '@shared-runtime/api';

import { SUB_ACCOUNT_UUID } from '../../constants/storage';
import {
  ENABLED_SUB_ACCOUNT_HEADER_APIS,
  SUB_ACCOUNT_HEADER,
} from '../../modules/account/constants/sub-accounts';

/**
 * Get sub-account UUID from sessionStorage
 * Returns undefined if not in browser or no sub-account is selected
 */
function getSubAccountUUID(): string | undefined {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return undefined;
  }
  return sessionStorage.getItem(SUB_ACCOUNT_UUID) || undefined;
}

/**
 * Browser API Client configuration
 */
export interface BrowserApiClientConfig extends BaseApiClientConfig {
  /**
   * Whether to enable sub-account header support
   * @default true
   */
  enableSubAccountHeader?: boolean;
}

/**
 * Browser API Client implementation
 * Extends BaseApiClient with browser-specific token retrieval from cookie
 * and sub-account header support
 */
export class BrowserApiClient extends BaseApiClient {
  constructor(config: BrowserApiClientConfig) {
    const enableSubAccount = config.enableSubAccountHeader !== false;

    super({
      ...config,
      // Override getToken to read from cookie
      getToken: () => Cookies.get('token'),
      // Configure sub-account header support
      subAccount: enableSubAccount
        ? {
            getSubAccountUUID,
            enabledApis: ENABLED_SUB_ACCOUNT_HEADER_APIS,
            headerName: SUB_ACCOUNT_HEADER,
          }
        : undefined,
    });
  }
}

/**
 * Create browser API client
 *
 * @param config - Client configuration
 * @returns BrowserApiClient instance
 *
 * @example
 * const client = createBrowserApiClient({
 *   baseUrl: 'https://api.example.com',
 *   headers: { 'X-Custom-Header': 'value' },
 *   timeout: 10000,
 *   onError: (error) => {
 *     toast.error(error.message);
 *   },
 * });
 */
export function createBrowserApiClient(
  config: BrowserApiClientConfig,
): BrowserApiClient {
  return new BrowserApiClient(config);
}
